import { router } from "@inertiajs/react"
import { Loader2, MoreHorizontal } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import type { RecordPageHeaderAction } from "./record-page-header"

interface RecordPageHeaderMenuProps {
  editPath?: string
  deletePath?: string
  deleteDisabledReason?: string
  actions?: RecordPageHeaderAction[]
}

export default function RecordPageHeaderMenu({
  editPath,
  deletePath,
  deleteDisabledReason,
  actions = [],
}: RecordPageHeaderMenuProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [processing, setProcessing] = useState(false)

  const handleAction = (action: RecordPageHeaderAction) => {
    if (action.onClick) {
      action.onClick()
    } else if (action.href) {
      router.visit(action.href)
    }
  }

  const handleDeleteConfirm = () => {
    if (!deletePath) return
    setProcessing(true)
    router.delete(deletePath, {
      onError: () => {
        toast.error("Failed to delete record")
      },
      onFinish: () => {
        setProcessing(false)
        setShowDeleteDialog(false)
      },
    })
  }

  const inlineActions = actions.filter((action) => action.inline)
  const menuActions = actions.filter((action) => !action.inline)

  return (
    <>
      <div className="flex items-center gap-4">
        {inlineActions.map((action) => (
          <Button
            key={action.label}
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => handleAction(action)}
          >
            {action.label}
          </Button>
        ))}
        {editPath && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => router.visit(editPath)}
          >
            Edit
          </Button>
        )}
        {(menuActions.length > 0 || deletePath) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="rounded-sm px-2">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {menuActions.map((action, index) => (
                <DropdownMenuItem
                  key={index}
                  onSelect={() => handleAction(action)}
                >
                  {action.label}
                </DropdownMenuItem>
              ))}
              {menuActions.length > 0 && deletePath && (
                <DropdownMenuSeparator />
              )}
              {deletePath &&
                (deleteDisabledReason ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* Disabled items don't receive pointer events, so the
                          wrapper span anchors the tooltip. */}
                      <span className="block">
                        <DropdownMenuItem disabled className="text-red-600">
                          Delete
                        </DropdownMenuItem>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {deleteDisabledReason}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <DropdownMenuItem
                    className="text-red-600"
                    onSelect={() => setShowDeleteDialog(true)}
                  >
                    Delete
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90 text-white"
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
